import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  type Firestore,
} from 'firebase/firestore';
import type { BillingSubscription } from '@kawai/shared';
import { db as defaultDb } from '../firebase.js';

/**
 * TP-23: billing_subscriptions collection 全件取得（read 専用 / saas-admin 用）。
 *
 * - Firestore Rules で read は `isAdmin() && belongsToMyTenant()`（saas-admin はバイパスで全件可）
 * - doc id = tenantId（1 テナント 1 サブスク）
 * - 解約予約中（status='canceled' かつ endedAt=null）の判定に使う（tenants.billingStatus は
 *   解約予約中も active のままのため、billing_subscriptions を見ないと再開可否が分からない）
 * - 引数 `db` はテスト時に Firestore モックを注入するため optional
 */
export async function fetchAllBillingSubscriptions(
  db: Firestore = defaultDb,
): Promise<BillingSubscription[]> {
  const snap = await getDocs(query(collection(db, 'billing_subscriptions')));
  return snap.docs.map((d) => ({
    ...(d.data() as BillingSubscription),
    // doc id = tenantId を真実源にする（data.tenantId と一致するが防御的に上書き）。
    tenantId: d.id,
  }));
}

/**
 * 単一テナントの billing_subscriptions doc 取得（read 専用 / saas-admin 用）。
 *
 * - doc id = tenantId（1 テナント 1 サブスク）なので getDoc 1 回で済む
 * - 明細ページ（`/billing/:tenantId`）で全件取得を避けて O(1) read にする
 * - 未開始（doc 無し）は null
 * - 引数 `db` はテスト時に Firestore モックを注入するため optional
 */
export async function fetchBillingSubscriptionByTenant(
  tenantId: string,
  db: Firestore = defaultDb,
): Promise<BillingSubscription | null> {
  const snap = await getDoc(doc(db, 'billing_subscriptions', tenantId));
  if (!snap.exists()) return null;
  return { ...(snap.data() as BillingSubscription), tenantId: snap.id };
}
